<template>
  <transition name="slide-down">
    <div v-if="isVisible" class="reading-streak-notification16464">
      <div class="reading-streak-notification16464__container" @click="hide()">
        <p class="reading-streak-notification16464__text">
          You have a {{ streakLength }}-day streak. Keep it up!
        </p>
        <ReadingStreakIcon16464
          class="reading-streak-notification16464__icon"
        />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { App } from '@capacitor/app'

import { emitter } from '@/services/mitt'

import ReadingStreakIcon16464 from './ReadingStreakIcon16464.vue'

defineProps<{
  streakLength: number
}>()

const isVisible = ref(false)
const timeout = ref<number | null>(null)

onMounted(async () => {
  const appState = await App.getState()
  if (appState.isActive) {
    show()
  } else {
    emitter.on('appVisibilityChange', show)
  }
})

onUnmounted(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
  emitter.off('appVisibilityChange', show)
})

function show(): void {
  isVisible.value = true
  timeout.value = setTimeout(hide, 5000)
}

function hide(): void {
  isVisible.value = false
}
</script>

<style lang="scss" scoped>
.reading-streak-notification16464 {
  position: fixed;
  top: 100px;
  left: 0;
  z-index: 99;
  display: flex;
  justify-content: center;
  width: 100%;
  padding-inline: var(--container-padding);
  pointer-events: none;

  @media (max-width: $w-tablet-s) {
    top: 65px;
  }

  &__container {
    display: flex;
    align-items: center;
    gap: 40px;
    width: fit-content;
    padding: 16px 40px;
    border: 1px solid transparent;
    border-radius: 12px;
    background:
      linear-gradient(
          135deg,
          rgba(255, 200, 142, 1) 0%,
          rgba(255, 223, 121, 1) 100%
        )
        padding-box,
      linear-gradient(
          135deg,
          rgba(255, 231, 155, 1) 0%,
          rgba(255, 206, 155, 1) 100%
        )
        border-box;
    pointer-events: all;
    user-select: none;
    cursor: pointer;

    @media (max-width: $w-mobile-s) {
      width: 100%;
      justify-content: space-between;
      padding: 16px;
      gap: 0;
    }
  }

  &__text {
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    letter-spacing: -0.02em;
    margin-bottom: -2px;
    color: $c-black-15;
  }

  &__icon {
    flex-shrink: 0;
    width: 36px;
  }
}

.slide-down {
  &-enter {
    &-active {
      transition: all 0.3s linear(0, 0.7 15%, 1.05 33%, 1.1 50%, 1);
    }

    &-from {
      transform: translateY(-100px);
      opacity: 0.5;
    }

    &-to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  &-leave {
    &-active {
      transition: all 0.13s;
    }

    &-from {
      opacity: 1;
    }

    &-to {
      opacity: 0;
    }
  }
}
</style>
